/**
 * Types and constants for Admin Settings
 */
import { MenuVisibility, SiteRegionalSettings, PreferencesChoices, DashboardTabsVisibility, SupplierCardFieldsVisibility, DataImportTilesVisibility } from '../../../lib/api';

// Portal types
export const PORTAL_TYPES = [
  { value: '2', label: 'Supplier Portal' },
  { value: '1', label: 'Customer Portal' },
];

export interface ThemeSettings {
  companyName: string;
  loginTitle: string;
  defaultTheme: number;
  availableThemes: string[];
  backgroundColor: string;
  loginColor: string;
  fontColor: string;
  buttonColor: string;
  website: string;
  supportEmail: string;
  salesEmail: string;
  noReplyEmail: string;
  contactEmail: string;
  companyAddress: string;
  companyPhone: string;
  emailSubject: string;
  emailSignature: string;
}

export interface PlatformSettings {
  mainMenuType: string;
  freeMode: boolean;
  mainCompanyCanLoginAs: boolean;
  automationRelated: boolean;
  useDispositionInNCR: boolean;
  warehouseModule: boolean;
  turnEmailMAB: boolean;
  switchPartRepository: boolean;
  qualityMeasurePPK: boolean;
  restrictCountry: string;
  restrictCountryEnabled: boolean;
  menuVisibility: MenuVisibility;
  ncrTemplate: 1 | 2;
  // Upload Limits (Feature 5)
  maxUploadSizeMb: number;
  allowedFileTypes: string[];
  // Document Versioning (Feature 8)
  autoVersionDocuments: boolean;
  versionFormat: 'numeric' | 'semantic';
  retainOldVersions: boolean;
  // Notification Defaults (Feature 3)
  defaultNotificationPreferences: Record<string, boolean>;
  systemAlertEmails: string;
  // Supplier Onboarding
  supplierOnboardingEnabled: boolean;
  // Hierarchy Labels
  hierarchyLevel1Label: string;
  hierarchyLevel2Label: string;
  hierarchyLevel3Label: string;
  // Default landing page for approved supplier users
  supplierDefaultLandingPath: string;
  // Feature-flagged modules
  poLineSchedules: boolean;
}

export interface BarrattFlags {
  delivery_appeals: boolean;
  spend_leakage: boolean;
  bulk_upload: boolean;
  commodity_codes: boolean;
  allow_supplier_decline: boolean;
  show_forecast_total: boolean;
  portal_mode: 'standard' | 'barratt' | 'compliance';
}

export const DEFAULT_THEME_SETTINGS: ThemeSettings = {
  companyName: 'Helix',
  loginTitle: '',
  defaultTheme: 1,
  availableThemes: ['theme1', 'theme2'],
  backgroundColor: '#ffffff',
  loginColor: '#0066cc',
  fontColor: '#333333',
  buttonColor: '#0066cc',
  website: 'https://helix.com',
  supportEmail: 'support@helix.com',
  salesEmail: 'sales@helix.com',
  noReplyEmail: 'noreply@helix.com',
  contactEmail: '',
  companyAddress: '',
  companyPhone: '',
  emailSubject: 'Helix Reset Password',
  emailSignature: 'Helix Team',
};

export const DEFAULT_DASHBOARD_TABS: DashboardTabsVisibility = {
  'default': true,
  'group-kpi': true,
  'group-detail-kpi': true,
  'site-kpi': true,
  'cost': true,
  'emissions': true,
  'compliance': true,
  'future-forecast': true,
  'date-adherence': true,
  'expediting': true,
  'performance-ratings': true,
};

export const DEFAULT_SUPPLIER_DASHBOARD_TABS: DashboardTabsVisibility = {
  'default': true,
  'group-kpi': false,
  'group-detail-kpi': false,
  'site-kpi': false,
  'cost': true,
  'emissions': false,
  'compliance': true,
  'future-forecast': false,
  'date-adherence': true,
  'expediting': false,
  'performance-ratings': true,
};

export const DEFAULT_SUPPLIER_CARD_FIELDS: SupplierCardFieldsVisibility = {
  logo: true,
  risk_badge: true,
  otd: true,
  rating: true,
  questionnaire_status: true,
};

export const DEFAULT_DATA_IMPORT_TILES: DataImportTilesVisibility = {
  commodity: true,
  faultcodes: true,
  departments: true,
  warehouses: true,
  suppliers: true,
  supplier_additional: true,
  grns: true,
  pos: true,
  ncrs: true,
  forecasts: true,
  users: true,
};

export const DEFAULT_BARRATT_FLAGS: BarrattFlags = {
  delivery_appeals: false,
  spend_leakage: false,
  bulk_upload: false,
  commodity_codes: false,
  allow_supplier_decline: false,
  show_forecast_total: false,
  portal_mode: 'standard',
};

// Re-export for convenience
export type { MenuVisibility, SiteRegionalSettings, PreferencesChoices, DashboardTabsVisibility, SupplierCardFieldsVisibility, DataImportTilesVisibility };
